/**
 * Timed effects.
 *
 * One instance per kind per player, refreshed rather than stacked. Stacking
 * would let two Hex glyphs slow someone to a standstill and two Bulwarks
 * multiply to near-immunity, neither of which the balance table prices in.
 */
import { msToTicks } from "./math.js";
export function hasEffect(p, kind) {
    for (const e of p.effects)
        if (e.kind === kind)
            return true;
    return false;
}
export function findEffect(p, kind) {
    for (const e of p.effects)
        if (e.kind === kind)
            return e;
    return null;
}
/** Drops effects whose lifetime has run out. Called once per player per tick. */
export function expireEffects(p, tick) {
    if (p.effects.length === 0)
        return;
    let write = 0;
    for (let i = 0; i < p.effects.length; i++) {
        const e = p.effects[i];
        if (e === undefined)
            continue;
        if (tick < e.untilTick) {
            p.effects[write] = e;
            write++;
        }
    }
    p.effects.length = write;
}
/**
 * Applies (or refreshes) an effect. Returns the effect so callers can inspect
 * the resulting duration for the `effect_applied` event.
 *
 * Refresh takes the later expiry and the stronger magnitude — a weak lingering
 * slow must not be able to overwrite a strong fresh one, and vice versa.
 */
export function applyEffect(p, kind, durationMs, tick, sourceId, opts) {
    const untilTick = tick + msToTicks(durationMs);
    const magnitude = opts?.magnitude ?? 0;
    const existing = findEffect(p, kind);
    if (existing) {
        if (untilTick > existing.untilTick)
            existing.untilTick = untilTick;
        if (magnitude > existing.magnitude)
            existing.magnitude = magnitude;
        existing.sourceId = sourceId;
        existing.ax = opts?.ax ?? existing.ax;
        existing.ay = opts?.ay ?? existing.ay;
        existing.az = opts?.az ?? existing.az;
        return existing;
    }
    const effect = {
        kind,
        untilTick,
        magnitude,
        sourceId,
        ax: opts?.ax ?? 0,
        ay: opts?.ay ?? 0,
        az: opts?.az ?? 0,
    };
    p.effects.push(effect);
    return effect;
}
export function removeEffect(p, kind) {
    for (let i = p.effects.length - 1; i >= 0; i--) {
        if (p.effects[i]?.kind === kind)
            p.effects.splice(i, 1);
    }
}
/**
 * Combined move-speed multiplier.
 *
 * Speeds multiply; slows multiply; `slow_immune` (Volt's Overload, whose
 * description promises "immunity to slows") drops the slows entirely.
 */
export function speedMultiplier(p) {
    let m = 1;
    const immune = hasEffect(p, "slow_immune");
    for (const e of p.effects) {
        if (e.kind === "speed")
            m *= 1 + e.magnitude / 100;
        else if (e.kind === "slow" && !immune)
            m *= 1 - e.magnitude / 100;
    }
    return m < 0 ? 0 : m;
}
/**
 * Strongest damage reduction, as a percentage. Deliberately a max rather than a
 * product: two 50% sources should not become 75%.
 */
export function damageReductionPct(p) {
    let best = 0;
    for (const e of p.effects) {
        if (e.kind === "damage_reduction" && e.magnitude > best)
            best = e.magnitude;
    }
    return best > 100 ? 100 : best;
}
export function isStunned(p) {
    return hasEffect(p, "stun");
}
/** Hex's Null Zone bars ultimates. Skills and basics still fire. */
export function ultimatesSuppressed(p) {
    return hasEffect(p, "suppress_abilities");
}
export function dashBlocked(p) {
    return hasEffect(p, "no_dash");
}
export function isInvisible(p) {
    return hasEffect(p, "invisible");
}
/** Effect names for the snapshot. The client uses these for VFX only. */
export function effectNames(p) {
    const out = [];
    for (const e of p.effects)
        out.push(e.kind);
    return out;
}
//# sourceMappingURL=effects.js.map